import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorText, newRequestId } from "../../lib/api";
import { SITE, TEL } from "../../lib/site";

const Ctx = createContext({ open: () => {} });
export const useCallbackModal = () => useContext(Ctx);

export function CallbackProvider({ children }) {
  const [isOpen, setOpen] = useState(false);
  return (
    <Ctx.Provider value={{ open: () => setOpen(true) }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl border-0 p-0 overflow-hidden" data-testid="callback-modal">
          <div className="bg-ag-navy px-7 pb-6 pt-7 text-white">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold text-white">Être rappelé</DialogTitle>
              <DialogDescription className="text-blue-100">Laissez votre numéro, un membre de l'équipe vous rappelle pendant nos horaires d'ouverture.</DialogDescription>
            </DialogHeader>
          </div>
          <CallbackForm onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

function CallbackForm({ onDone }) {
  const [f, setF] = useState({ nom: "", telephone: "", creneau: "Dès que possible", service: "", consentement: false, website: "" });
  const [rid] = useState(newRequestId);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { ...f, type: "rappel", service: f.service || null, request_id: rid });
      toast.success("Demande envoyée. Nous vous rappelons très vite.");
      onDone();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4 px-7 pb-7 pt-5" data-testid="callback-modal-form">
      <input className="field" placeholder="Votre nom *" required value={f.nom} onChange={set("nom")} data-testid="callback-name-input" aria-label="Nom" />
      <input className="field" type="tel" placeholder="Votre téléphone *" required value={f.telephone} onChange={set("telephone")} data-testid="callback-phone-input" aria-label="Téléphone" />
      <select className="field" value={f.creneau} onChange={set("creneau")} data-testid="callback-slot-select" aria-label="Créneau de rappel">
        {["Dès que possible", "Matin", "Midi", "Après-midi"].map((o) => <option key={o}>{o}</option>)}
      </select>
      <input className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} aria-hidden="true" />
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <input type="checkbox" required checked={f.consentement} onChange={set("consentement")} className="mt-0.5" data-testid="callback-consent-checkbox" />
        J'accepte que mes données soient utilisées pour être recontacté(e). <a href="/politique-confidentialite" className="underline">Confidentialité</a>
      </label>
      <button disabled={busy} className="btn btn-blue w-full justify-center" data-testid="callback-submit-button">{busy ? "Envoi…" : "Me rappeler"}</button>
      <a href={TEL} className="flex items-center justify-center gap-2 text-sm font-semibold text-ag-navy" data-testid="callback-direct-call-link"><Phone className="h-4 w-4" /> ou appelez le {SITE.phone}</a>
    </form>
  );
}
